import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Tooltip } from "react-tooltip";
import { FingerPrintIcon, PlusIcon, Squares2X2Icon } from "@heroicons/react/24/outline";

import SidebarNavItem from "./SidebarNavItem";
import { BrandAvatar } from "./patterns/AdaptBrandSelector";
import MoreMenu from "./MoreMenu";
import { setCurrentBrand } from "../redux/brand/brandSlice";

const Sidebar = ({ isOpen, onClose, currentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentProject } = useSelector((state) => state.project);
  const { currentBrand, brands, ready: brandsReady } = useSelector(
    (state) => state.brand,
  );
  const projectsLoaded = useSelector((state) => state.project.loaded);
  const { sidebarCollapsed } = useSelector((state) => state.ui);
  const [brandMenuOpen, setBrandMenuOpen] = useState(false);
  const desktopBrandMenuRef = useRef(null);
  const mobileBrandMenuRef = useRef(null);

  const brandSelectorLoading =
    !projectsLoaded ||
    !currentProject?.hash ||
    !brandsReady ||
    (brands.length > 0 && !currentBrand?.hash);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDesktop = desktopBrandMenuRef.current?.contains(event.target);
      const inMobile = mobileBrandMenuRef.current?.contains(event.target);
      if (!inDesktop && !inMobile) {
        setBrandMenuOpen(false);
      }
    };
    if (brandMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [brandMenuOpen]);

  const handleBrandSelect = (brand) => {
    dispatch(setCurrentBrand(brand));
    setBrandMenuOpen(false);
    onClose?.();
  };

  const handleAddBrand = () => {
    setBrandMenuOpen(false);
    onClose?.();
    navigate("/brand/new/");
  };

  const mainNavigation = [
    {
      name: "Ideas",
      href: "/ideas/",
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
          />
        </svg>
      ),
    },
    {
      name: "Brand",
      href: "/brand/",
      icon: <FingerPrintIcon className="h-5 w-5" />,
    },
    {
      name: "Research",
      href: "/research/",
      icon: (
        <svg
          className="h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605"
          />
        </svg>
      ),
    },
    {
      name: "Scripts",
      href: "/scripts/",
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
          />
        </svg>
      ),
    },
    {
      name: "Videos",
      href: "/videos/",
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
          />
        </svg>
      ),
    },
  ];

  mainNavigation.push({
    name: "API",
    href: "/api/",
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
        />
      </svg>
    ),
  });

  const bottomNavigation = [
    ...(brandsReady && brands.length > 1
      ? [{ name: "Brands", href: "/brands/", icon: <Squares2X2Icon className="h-5 w-5" /> }]
      : []),
    {
      name: "Account",
      href: "/account/",
      icon: (
        <svg
          className="h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
          />
        </svg>
      ),
    },
  ];

  const isNavActive = (href) => {
    if (href === "/brands/") {
      return location.pathname === "/brands/" || location.pathname.startsWith("/brands/");
    }
    if (href === "/brand/") {
      return (
        location.pathname.startsWith("/brand/")
        && !location.pathname.startsWith("/brands/")
      );
    }
    if (href === "/account/") {
      return location.pathname.startsWith("/account/");
    }
    return location.pathname === href || location.pathname.startsWith(href);
  };

  const brandSelector = (collapsed, menuRef) => {
    return (
    <div
      ref={menuRef}
      className={`border-b border-theme-secondary ${collapsed ? "px-2 py-3" : "px-3 py-3"}`}
    >
      <div className="relative">
        <button
          type="button"
          onClick={() => setBrandMenuOpen((open) => !open)}
          className={`w-full flex items-center rounded-lg border border-theme-secondary bg-theme-main text-left text-theme-text hover:opacity-90 ${
            collapsed ? "justify-center px-2 py-2" : "px-3 py-2"
          }`}
          data-tooltip-id={collapsed ? "brand-tooltip" : undefined}
          data-tooltip-content={
            collapsed ? currentBrand?.name || "Select brand" : undefined
          }
        >
          {currentBrand?.hash ? (
            <BrandAvatar brand={currentBrand} size="sm" fallback="fingerprint" />
          ) : (
            <FingerPrintIcon className="h-4 w-4 flex-shrink-0 text-theme-text opacity-50" />
          )}
          {!collapsed && (
            <span className="ml-2 flex-1 truncate text-sm font-medium">
              {brandSelectorLoading
                ? "Loading..."
                : currentBrand?.name || "Select brand"}
            </span>
          )}
          {!collapsed && (
            <svg
              className="h-4 w-4 opacity-60"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
              />
            </svg>
          )}
        </button>
        {collapsed && (
          <Tooltip id="brand-tooltip" place="right" className="z-50" />
        )}

        {brandMenuOpen && (
          <div
            className={`absolute z-50 rounded-lg border border-theme-secondary bg-theme-main shadow-lg ${
              collapsed
                ? "left-full ml-2 top-0 mt-0 w-56"
                : "left-0 right-0 top-full mt-2"
            }`}
          >
            <div className="max-h-64 overflow-y-auto py-1">
              {brands.map((brand) => (
                <button
                  key={brand.hash}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleBrandSelect(brand);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-10 ${
                    currentBrand?.hash === brand.hash
                      ? "bg-black bg-opacity-10 font-medium"
                      : ""
                  }`}
                >
                  <BrandAvatar brand={brand} size="sm" fallback="fingerprint" />
                  <span className="truncate">{brand.name || "Unnamed brand"}</span>
                </button>
              ))}
              {!brands.length && !brandSelectorLoading ? (
                <p className="px-3 py-2 text-sm text-theme-text opacity-60">
                  No brands yet
                </p>
              ) : null}
            </div>
            <div className="border-t border-theme-secondary py-1">
              <button
                type="button"
                onClick={handleAddBrand}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-theme-primary hover:bg-black hover:bg-opacity-10"
              >
                <PlusIcon className="h-4 w-4 flex-shrink-0" />
                Add brand
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    );
  };

  return (
    <>
      <aside className="hidden lg:flex lg:flex-shrink-0 overflow-visible h-screen">
        <div
          className={`flex flex-col h-full border-r border-theme-secondary bg-theme-secondary transition-all duration-300 overflow-visible ${
            sidebarCollapsed ? "w-16" : "w-64"
          }`}
        >
          <div
            className={`flex items-center h-16 border-b border-theme-secondary ${
              sidebarCollapsed ? "justify-center px-2" : "px-6"
            }`}
          >
            <Link
              to="/"
              className="flex items-center cursor-pointer"
              data-tooltip-id="logo-tooltip"
              data-tooltip-content="MoonScroll"
            >
              <svg
                className="h-8 w-8"
                viewBox="0 0 54 33"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M27 0C12 0 0 12 0 27C0 29.2091 0.234219 31.3507 0.680627 33.4C1.12703 31.3507 1.36125 29.2091 1.36125 27C1.36125 13.8579 12.3579 2.86125 25.5 2.86125C38.6421 2.86125 49.6387 13.8579 49.6387 27C49.6387 29.2091 49.873 31.3507 50.3194 33.4C50.7658 31.3507 51 29.2091 51 27C51 12 39 0 27 0Z"
                  fill="var(--theme-primary)"
                />
                <path
                  d="M27 13.5C20.0964 13.5 14.5 19.0964 14.5 26C14.5 27.3807 14.7801 28.6904 15.2801 29.9C15.7801 28.6904 16.0602 27.3807 16.0602 26C16.0602 20.4772 20.4772 16.0602 26 16.0602C31.5228 16.0602 35.9398 20.4772 35.9398 26C35.9398 27.3807 36.2199 28.6904 36.7199 29.9C37.2199 28.6904 37.5 27.3807 37.5 26C37.5 19.0964 31.9036 13.5 25 13.5H27Z"
                  fill="var(--theme-primary)"
                />
              </svg>
              {!sidebarCollapsed && (
                <span className="ml-2 text-lg font-semibold text-theme-text">
                  MoonScroll
                </span>
              )}
            </Link>
            {sidebarCollapsed && (
              <Tooltip id="logo-tooltip" place="right" className="z-50" />
            )}
          </div>

          {brandSelector(sidebarCollapsed, desktopBrandMenuRef)}

          <nav
            className={`flex-1 py-4 space-y-1 overflow-visible ${
              sidebarCollapsed ? "px-2" : "px-3"
            }`}
          >
            {mainNavigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                isActive={isNavActive(item.href)}
                onClick={onClose}
                collapsed={sidebarCollapsed}
              />
            ))}
          </nav>

          <div
            className={`py-4 border-t border-theme-secondary space-y-1 overflow-visible ${
              sidebarCollapsed ? "px-2" : "px-3"
            }`}
          >
            {bottomNavigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                isActive={isNavActive(item.href)}
                onClick={onClose}
                collapsed={sidebarCollapsed}
              />
            ))}
            <MoreMenu collapsed={sidebarCollapsed} onClose={onClose} />
          </div>
        </div>
      </aside>

      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 border-r border-theme-secondary bg-theme-secondary transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b border-theme-secondary">
            <Link to="/" className="flex items-center cursor-pointer">
              <svg
                className="h-8 w-8"
                viewBox="0 0 54 33"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M27 0C12 0 0 12 0 27C0 29.2091 0.234219 31.3507 0.680627 33.4C1.12703 31.3507 1.36125 29.2091 1.36125 27C1.36125 13.8579 12.3579 2.86125 25.5 2.86125C38.6421 2.86125 49.6387 13.8579 49.6387 27C49.6387 29.2091 49.873 31.3507 50.3194 33.4C50.7658 31.3507 51 29.2091 51 27C51 12 39 0 27 0Z"
                  fill="var(--theme-primary)"
                />
                <path
                  d="M27 13.5C20.0964 13.5 14.5 19.0964 14.5 26C14.5 27.3807 14.7801 28.6904 15.2801 29.9C15.7801 28.6904 16.0602 27.3807 16.0602 26C16.0602 20.4772 20.4772 16.0602 26 16.0602C31.5228 16.0602 35.9398 20.4772 35.9398 26C35.9398 27.3807 36.2199 28.6904 36.7199 29.9C37.2199 28.6904 37.5 27.3807 37.5 26C37.5 19.0964 31.9036 13.5 25 13.5H27Z"
                  fill="var(--theme-primary)"
                />
              </svg>
              <span className="ml-2 text-lg font-semibold text-theme-text">
                MoonScroll
              </span>
            </Link>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-theme-text opacity-70 hover:opacity-100 hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-theme-primary"
              aria-label="Close sidebar"
            >
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {brandSelector(false, mobileBrandMenuRef)}

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {mainNavigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                isActive={isNavActive(item.href)}
                onClick={onClose}
              />
            ))}
          </nav>

          <div className="px-3 py-4 border-t border-theme-secondary space-y-1">
            {bottomNavigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                isActive={isNavActive(item.href)}
                onClick={onClose}
              />
            ))}
            <MoreMenu showSidebarToggle={false} onClose={onClose} />
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
